import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { partService } from '../../services/partService';

/**
 * Admin: list and remove part categories from the shared catalog.
 * Removing a category also clears it on any parts that used it.
 */
const PartCategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [removing, setRemoving] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await partService.getCategories();
      setCategories(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load part categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRemove = async (name) => {
    if (!window.confirm(`Remove category "${name}"? Parts using it will become Uncategorized.`)) {
      return;
    }
    try {
      setRemoving(name);
      setError('');
      setSuccess('');
      const res = await partService.removeCategory(name);
      const cleared = res?.data?.parts_cleared ?? 0;
      setSuccess(
        cleared > 0
          ? `Removed "${name}" and cleared it on ${cleared} part${cleared === 1 ? '' : 's'}.`
          : `Removed "${name}".`
      );
      await load();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to remove category');
    } finally {
      setRemoving('');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading categories…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Categories appear in the Parts filter and edit dropdown. Removing one clears it from any parts that used it.
      </Typography>
      {categories.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No categories yet. Create one when editing a part (New Category…).
        </Typography>
      ) : (
        <List dense disablePadding>
          {categories.map((name) => (
            <ListItem
              key={name}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label={`Remove ${name}`}
                  onClick={() => handleRemove(name)}
                  disabled={!!removing}
                  size="small"
                >
                  {removing === name ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              }
              sx={{ borderBottom: '1px solid', borderColor: 'divider', pr: 6 }}
            >
              <ListItemText primary={name} />
            </ListItem>
          ))}
        </List>
      )}
      <Button size="small" onClick={load} disabled={loading || !!removing} sx={{ mt: 1 }}>
        Refresh
      </Button>
    </Box>
  );
};

export default PartCategoriesManager;
